import { Selection } from '@typeRoots/common/checkListModal';
import { Loss } from '@typeRoots/common/loss';

// Calculate total loss amount
export const calculateTotalAmount = (loss: Loss) => {
  const amounts = [
    loss.lossPaidAutoLiability?.amount || 0,
    loss.lossPaidAutoPhysicalDamage?.amount || 0,
    loss.lossPaidMotorTruckCargo?.amount || 0,
    loss.lossPaidTruckingGeneralLiability?.amount || 0,
    loss.lossPaidNonTruckingLiability?.amount || 0,
  ];

  const total = amounts.reduce((sum, amount) => sum + amount, 0);

  return `$${total.toLocaleString()}`;
};

// Get selected claims based on count fields
export const getSelectedClaims = (loss: Loss) => {
  const selectedClaims: string[] = [];
  Object.entries(claimFieldMapping).forEach(([claimName, mapping]) => {
    if (loss[mapping.countKey] > 0) {
      selectedClaims.push(claimName);
    }
  });
  return selectedClaims;
};

// Field mapping for claims to form input fields
export const claimFieldMapping: {
  [key: string]: {
    label: string;
    key: keyof Pick<
      Loss,
      | 'lossPaidAutoLiability'
      | 'lossPaidAutoPhysicalDamage'
      | 'lossPaidMotorTruckCargo'
      | 'lossPaidNonTruckingLiability'
      | 'lossPaidTruckingGeneralLiability'
    >;
    countKey: keyof Pick<
      Loss,
      | 'lossCountAutoLiability'
      | 'lossCountAutoPhysicalDamage'
      | 'lossCountMotorTruckCargo'
      | 'lossCountNonTruckingLiability'
      | 'lossCountTruckingGeneralLiability'
    >;
  };
} = {
  'Automobile Liability': {
    label: 'Amount Paid for Automobile Liability',
    key: 'lossPaidAutoLiability',
    countKey: 'lossCountAutoLiability',
  },
  'Automobile Physical Damage': {
    label: 'Amount Paid for Automobile Physical Damage',
    key: 'lossPaidAutoPhysicalDamage',
    countKey: 'lossCountAutoPhysicalDamage',
  },
  'Motor Truck Cargo': {
    label: 'Amount Paid for Motor Truck Cargo',
    key: 'lossPaidMotorTruckCargo',
    countKey: 'lossCountMotorTruckCargo',
  },
  'Non-Trucking Liability': {
    label: 'Amount Paid for Non-Trucking Liability',
    key: 'lossPaidNonTruckingLiability',
    countKey: 'lossCountNonTruckingLiability',
  },
  'General Liability': {
    label: 'Amount Paid for General Liability',
    key: 'lossPaidTruckingGeneralLiability',
    countKey: 'lossCountTruckingGeneralLiability',
  },
};

export const resetLossCounts = (loss: Loss, selection: Selection[]): Loss => {
  const updatedLoss = { ...loss };

  Object.values(claimFieldMapping).forEach((mapping) => {
    updatedLoss[mapping.countKey] = 0;
  });

  // Reset amount of claims that were deselected
  Object.keys(claimFieldMapping).forEach((claim) => {
    const fieldKey = claimFieldMapping[claim]?.key;
    if (fieldKey && !selection.some((c) => c.subtype.name === claim)) {
      updatedLoss[fieldKey] = { amount: 0, currency: 'USD' };
    }
  });

  // update count of claims that are selected
  selection.forEach((selectedClaim) => {
    const mapping = claimFieldMapping[selectedClaim.subtype.name];
    if (mapping) {
      updatedLoss[mapping.countKey] = 1;
    }
  });

  return updatedLoss;
};

export const updateLossPayload = (loss: Loss, companyId: number): Loss => {
  return {
    ...loss,
    company: { id: companyId },
    lossConfirmed: true,
  };
};

export const mapSelectedClaims = (loss: Loss) => {
  const claimMappings = [
    {
      key: 'lossCountAutoLiability',
      type: 'AutomobileLiability',
      name: 'Automobile Liability',
      value: 'automobile_liability',
    },
    {
      key: 'lossCountAutoPhysicalDamage',
      type: 'AutomobilePhysicalDamage',
      name: 'Automobile Physical Damage',
      value: 'automobile_physical_damage',
    },
    {
      key: 'lossCountMotorTruckCargo',
      type: 'MotorTruckCargo',
      name: 'Motor Truck Cargo',
      value: 'motor_truck_cargo',
    },
    {
      key: 'lossCountTruckingGeneralLiability',
      type: 'GeneralLiability',
      name: 'General Liability',
      value: 'general_liability',
    },
    {
      key: 'lossCountNonTruckingLiability',
      type: 'NonTruckingLiability',
      name: 'Non-Trucking Liability',
      value: 'non_trucking_liability',
    },
  ];

  return claimMappings
    .filter(({ key }) => {
      const countValue = loss[key as keyof Loss]; // Extract value using the key
      return typeof countValue === 'number' && countValue > 0; // Ensure it's a number before comparing
    })
    .map(({ type, name, value }) => ({
      type,
      subtype: { name, value },
    }));
};
